import { sanitizeErrorMessage } from '@exodus/errors';
import { isNumberUnit } from '@exodus/currency';
import lodash from 'lodash';
const { cloneDeepWith } = lodash;
const toJSON = (error) => {
    const serializedDetails = error.details
        ? cloneDeepWith(error.details, (val) => {
            if (val && isNumberUnit(val))
                return val.toDefaultString({ unit: true });
            if (typeof val === 'string')
                return sanitizeErrorMessage(val);
        })
        : null;
    return {
        name: error.name,
        message: sanitizeErrorMessage(error.message),
        ...(serializedDetails ? { details: serializedDetails } : {}),
        ...(process.env.NODE_ENV === 'test'
            ? {}
            : { stack: sanitizeErrorMessage(error.cause?.stack || error.stack) }),
    };
};
export class ExchangeError extends Error {
    name = 'Exchange Error';
    isExchange = true;
    details;
    constructor({ message, details }, opts) {
        super(message, opts);
        this.details = details;
    }
    toJSON = () => toJSON(this);
}
export class ExchangeOrderError extends ExchangeError {
    name = 'Exchange Order Error';
    orderId;
    constructor({ message, orderId, details }, opts) {
        super({ message, details }, opts);
        this.orderId = orderId;
    }
}
export class CreateOrderError extends ExchangeOrderError {
    name = 'Create Order Error';
}
export class CreateDexOrderError extends ExchangeError {
    name = 'Create Dex Order Error';
}
export class GetProviderOrderError extends ExchangeOrderError {
    name = 'Get Provider Order Error';
}
export class OrderValidationError extends ExchangeOrderError {
    name = 'Order Validation Error';
    constructor({ expected, actual, order, providerOrder }, opts) {
        const details = {
            order,
            providerOrder,
        };
        const message = `expected ${expected}, got ${actual}`;
        super({ message, details, orderId: order.id }, opts);
    }
}
export class JupiterSimulationError extends ExchangeOrderError {
    name = 'Jupiter Simulation Error';
}
export class InvalidAmountError extends OrderValidationError {
    name = 'Invalid Amount Error';
}
export class InvalidFromTokenAddressError extends OrderValidationError {
    name = 'Invalid From Token Address Error';
}
export class InvalidToTokenAddressError extends OrderValidationError {
    name = 'Invalid To Token Address Error';
}
export class InvalidPayInAddressError extends ExchangeOrderError {
    name = 'Invalid Pay In Address Error';
}
export class CreateTxError extends ExchangeOrderError {
    name = 'Create Tx Error';
}
export class CreateDexTxError extends ExchangeOrderError {
    name = 'Create Tx Error';
}
export class SendTxError extends ExchangeError {
    name = 'Send Tx Error';
}
export class SignTxError extends ExchangeError {
    name = 'Sign Tx Error';
}
export class GetAllowanceError extends ExchangeError {
    name = 'Get Allowance Error';
    constructor({ message, assetId, approvalAddress }, opts) {
        const details = {
            assetId,
            approvalAddress,
        };
        super({ message, details }, opts);
    }
}
export class ApproveTokensError extends ExchangeError {
    name = 'Approve Tokens Error';
    constructor({ message, assetId, approvalAddress, amount }, opts) {
        const details = {
            assetId,
            approvalAddress,
            amount: amount.toDefaultString(),
        };
        super({ message, details }, opts);
    }
}
export class ExchangeLimitsError extends ExchangeError {
    name = 'Exchange Limits Error';
}
export class MissingPairError extends ExchangeError {
    name = 'Missing Pair Error';
    constructor({ fromAsset, toAsset, pairId }) {
        const message = `Pair ${pairId} not found, ${fromAsset === undefined ? 'from asset is missing' : ``} ${toAsset === undefined ? 'to asset is missing' : ``}`;
        super({ message });
    }
}
export class MissingToAssetError extends ExchangeError {
    name = 'Missing To Asset Error';
    orderId;
    constructor({ toAsset, orderId }) {
        const message = `To asset ${toAsset} is missing`;
        super({ message });
        this.orderId = orderId;
    }
}
export class MissingFromAssetError extends ExchangeError {
    name = 'Missing From Asset Error';
    orderId;
    constructor({ fromAsset, orderId }) {
        const message = `From asset ${fromAsset} is missing`;
        super({ message });
        this.orderId = orderId;
    }
}
export class MissingFeeAssetError extends ExchangeError {
    name = 'Missing Fee Asset Error';
    constructor({ feeAsset }) {
        const message = `Fee asset ${feeAsset} is missing`;
        super({ message });
    }
}
export class MissingPairPriceError extends ExchangeError {
    name = 'Missing Pair Price Error';
    constructor({ pairId, fromAssetPrice, toAssetPrice }) {
        const message = `Pair price for ${pairId} is missing, ${fromAssetPrice === undefined ? 'from asset price is missing' : ``} ${toAssetPrice === undefined ? 'to asset price is missing' : ``}`;
        super({ message });
    }
}
export class JupiterProviderError extends ExchangeError {
    name = 'Jupiter Provider Error';
}
export class EVMSimulationError extends ExchangeError {
    name = 'EVM Simulation Error';
}
export class LifiProviderError extends ExchangeError {
    name = 'Lifi Provider Error';
}
export class TransactionDecoderError extends ExchangeError {
    name = 'Transaction Decoder Error';
}
export class NoRateAvailableError extends ExchangeError {
    name = 'No Rate Available';
    constructor({ amount, min, max }) {
        const message = `No rate available for amount ${amount}, min ${min}, max ${max}`;
        super({ message });
    }
}
export class NoQuoteAvailableError extends ExchangeError {
    name = 'No Quote Available';
    constructor({ minValue, maxValue, minAssetId, maxAssetId }) {
        const message = `No quote available for min value ${minValue}, max value ${maxValue}, min asset ${minAssetId}, max asset ${maxAssetId}`;
        super({ message });
    }
}
export class MissingProviderOrderError extends ExchangeError {
    name = 'Missing Provider Order Error';
}
export class RangoProviderError extends ExchangeError {
    name = 'Rango Provider Error';
}
export class AssetsServiceError extends ExchangeError {
    name = 'Assets Service Error';
}
export class ProviderVerifiersError extends ExchangeError {
    name = 'Provider Verifiers Error';
}
